var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2023 Félix Robles <felix@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { CandidatesList } from "@sequentech/ui-essentials";
import { isUndefined, translate, keyBy } from "@sequentech/ui-core";
import { Answer } from "../Answer/Answer";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { resetBallotSelection, selectBallotSelectionQuestion, selectBallotSelectionVoteChoice, setBallotSelectionVoteChoice, } from "../../store/ballotSelections/ballotSelectionsSlice";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";
import { sortCandidatesInContest } from "@sequentech/ui-core";
var showCategoryOnReview = function (category, questionState) {
    if (isUndefined(questionState)) {
        return false;
    }
    var answersFromCategory = category.candidates.map(function (candidate) { return candidate.id; });
    if (!isUndefined(category.header)) {
        answersFromCategory.push(category.header.id);
    }
    return questionState.choices.some(function (choice) { return choice.selected > -1 && answersFromCategory.includes(choice.id); });
};
export var AnswersList = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var title = _a.title, isActive = _a.isActive, checkableLists = _a.checkableLists, checkableCandidates = _a.checkableCandidates, iconCheckboxPolicy = _a.iconCheckboxPolicy, category = _a.category, ballotStyle = _a.ballotStyle, contestId = _a.contestId, isReview = _a.isReview, isInvalidWriteIns = _a.isInvalidWriteIns, isRadioSelection = _a.isRadioSelection, contest = _a.contest, selectedChoicesSum = _a.selectedChoicesSum, setSelectedChoicesSum = _a.setSelectedChoicesSum, disableSelect = _a.disableSelect, explicitBlank = _a.explicitBlank, setExplicitBlank = _a.setExplicitBlank, setIsTouched = _a.setIsTouched;
    var categoryAnswerId = ((_b = category.header) === null || _b === void 0 ? void 0 : _b.id) || "";
    var selectionState = useAppSelector(selectBallotSelectionVoteChoice(ballotStyle.election_id, contestId, categoryAnswerId));
    var questionState = useAppSelector(selectBallotSelectionQuestion(ballotStyle.election_id, contestId));
    var dispatch = useAppDispatch();
    var i18n = useTranslation().i18n;
    var _j = useState(null), candidatesOrder = _j[0], setCandidatesOrder = _j[1];
    var candidatesOrderType = (_c = contest.presentation) === null || _c === void 0 ? void 0 : _c.candidates_order;
    var isChecked = function () { return !isUndefined(selectionState) && selectionState.selected > -1; };
    var setChecked = function (value) {
        if (isRadioSelection) {
            dispatch(resetBallotSelection({
                ballotStyle: ballotStyle,
                force: true,
                contestId: contest.id,
            }));
        }
        return (isActive &&
            dispatch(setBallotSelectionVoteChoice({
                ballotStyle: ballotStyle,
                contestId: contestId,
                voteChoice: {
                    id: categoryAnswerId,
                    selected: value ? 0 : -1,
                },
            })));
    };
    if (isReview && !showCategoryOnReview(category, questionState)) {
        return null;
    }
    if (null === candidatesOrder) {
        setCandidatesOrder(sortCandidatesInContest(category.candidates, candidatesOrderType, true).map(function (c) { return c.id; }));
    }
    var categoryCandidatesMap = keyBy(category.candidates, "id");
    var listPresentation = (_f = (_e = (_d = contest.presentation) === null || _d === void 0 ? void 0 : _d.types_presentation) === null || _e === void 0 ? void 0 : _e[title]) !== null && _f !== void 0 ? _f : {
        name: title,
    };
    listPresentation.name = title;
    var subtypesPresentation = Object.entries((_g = listPresentation.subtypes_presentation) !== null && _g !== void 0 ? _g : {}).map(function (_a) {
        var _b;
        var key = _a[0], value = _a[1];
        value.name = key;
        value.sort_order = (_b = value.sort_order) !== null && _b !== void 0 ? _b : 0;
        return value;
    });
    var sortedSubtypes = sortBy(subtypesPresentation, ["sort_order"]);
    var shouldDisableList = disableSelect && !isChecked();
    return (_jsxs(CandidatesList, __assign({ title: (_h = translate(listPresentation, "name", i18n.language)) !== null && _h !== void 0 ? _h : title, isActive: !isReview && isActive, isCheckable: checkableLists, checked: isChecked(), setChecked: setChecked, shouldDisable: shouldDisableList }, { children: [sortedSubtypes.map(function (subtypePresentation) {
                var _a;
                var subtypeCandidates = (_a = candidatesOrder === null || candidatesOrder === void 0 ? void 0 : candidatesOrder.map(function (id) { return categoryCandidatesMap[id]; }).filter(function (candidate) { var _a; return subtypePresentation.name === ((_a = candidate.presentation) === null || _a === void 0 ? void 0 : _a.subtype); })) !== null && _a !== void 0 ? _a : [];
                var subtypeCandidateIds = subtypeCandidates.map(function (candidate) { return candidate.id; });
                var hasSelectedAnswer = questionState === null || questionState === void 0 ? void 0 : questionState.choices.some(function (choice) { return choice.selected > -1 && subtypeCandidateIds.includes(choice.id); });
                if (0 === subtypeCandidates.length || (isReview && !hasSelectedAnswer)) {
                    return null;
                }
                return (_jsxs(_Fragment, { children: [_jsx("b", { children: translate(subtypePresentation, "name", i18n.language) }), subtypeCandidates.map(function (candidate, candidateIndex) { return (_jsx(Answer, { ballotStyle: ballotStyle, answer: candidate, contestId: contestId, index: candidateIndex, hasCategory: true, isActive: !isReview && checkableCandidates, isReview: isReview, isInvalidVote: false, isInvalidWriteIns: isInvalidWriteIns, contest: contest, selectedChoicesSum: selectedChoicesSum, setSelectedChoicesSum: setSelectedChoicesSum, disableSelect: disableSelect, iconCheckboxPolicy: iconCheckboxPolicy, explicitBlank: explicitBlank, setExplicitBlank: setExplicitBlank, setIsTouched: setIsTouched }, candidateIndex)); })] }));
            }), candidatesOrder === null || candidatesOrder === void 0 ? void 0 : candidatesOrder.map(function (id) { return categoryCandidatesMap[id]; }).filter(function (candidate) { var _a; return !((_a = candidate.presentation) === null || _a === void 0 ? void 0 : _a.subtype); }).map(function (candidate, candidateIndex) { return (_jsx(Answer, { ballotStyle: ballotStyle, answer: candidate, contestId: contestId, index: candidateIndex, hasCategory: true, isActive: !isReview && checkableCandidates, isReview: isReview, isInvalidVote: false, isInvalidWriteIns: isInvalidWriteIns, contest: contest, selectedChoicesSum: selectedChoicesSum, setSelectedChoicesSum: setSelectedChoicesSum, disableSelect: disableSelect, iconCheckboxPolicy: iconCheckboxPolicy, explicitBlank: explicitBlank, setExplicitBlank: setExplicitBlank, setIsTouched: setIsTouched }, candidateIndex)); })] })));
};
